/**
 * GA URL sanitization (MOD-132).
 *
 * gtag auto-populates `page_location` from `document.location.href` — query
 * string and hash included — unless we override it. The marketing site must
 * never leak a `?query` (e.g. the `?t=<token>` recovery-link class flagged in
 * TODOS.md) to Google. Every GA pageview is built from these pure helpers so
 * the guarantee is unit-testable, not a runtime hope.
 *
 *   raw href ──▶ sanitizeLocation ──▶ origin + normalized pathname (no ?,#)
 *   pathname ──▶ normalizePath    ──▶ trailing-slash-normalized path
 *
 * `next.config.ts` sets `skipTrailingSlashRedirect: true`, so `/our-story` and
 * `/our-story/` can both be live URLs — normalizePath collapses them so the
 * privacy assertions can't pass against a path real users don't hit.
 */

/** Collapse duplicate slashes and strip a trailing slash (except root). */
export function normalizePath(pathname: string): string {
  const stripped = pathname.replace(/\/{2,}/g, '/').replace(/(.)\/+$/, '$1')
  return stripped === '' ? '/' : stripped
}

/**
 * Reduce any URL (or bare pathname) to `origin + normalizedPathname`, dropping
 * the query string and hash entirely.
 */
export function sanitizeLocation(href: string): string {
  try {
    const url = new URL(href)
    return `${url.origin}${normalizePath(url.pathname)}`
  } catch {
    // Not an absolute URL — treat as a path, drop ? and # ourselves.
    const pathOnly = href.split(/[?#]/)[0] || '/'
    return normalizePath(pathOnly)
  }
}
